import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import {
  Ticket, MessageSquare, Clock, User, Shield, ChevronLeft,
  Send, Loader2, Trash2, CheckCircle, AlertCircle, XCircle,
  Filter, RefreshCw, Pencil, X, Check, CheckSquare, Square, ListChecks
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import StatCard from "./StatCard";

const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: "Ouvert", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", icon: AlertCircle },
  answered: { label: "Répondu", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: CheckCircle },
  closed: { label: "Fermé", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", icon: XCircle },
};

const PRIORITY_CONFIG = {
  low: { label: "Basse", color: "text-zinc-500" },
  normal: { label: "Normale", color: "text-cyan-400" },
  high: { label: "Haute", color: "text-red-400" },
};

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  });
}

function formatRelative(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `${diffMins}min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}j`;
  return formatDate(dateString);
}

export default function TicketsContent({ token }) {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTickets = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await axios.get(`${API}/admin/tickets`, { headers: { Authorization: `Bearer ${token}` }, params });
      setTickets(res.data);
    } catch { /* silent */ }
    finally { if (!silent) setIsLoading(false); }
  }, [token, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/tickets/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch { /* silent */ }
  }, [token]);

  const fetchTicketDetail = useCallback(async (ticketId, silent = false) => {
    if (!silent) setIsLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/admin/tickets/${ticketId}`, { headers: { Authorization: `Bearer ${token}` } });
      setTicketDetail(res.data);
    } catch {
      if (!silent) toast.error("Erreur lors du chargement");
    } finally { if (!silent) setIsLoadingDetail(false); }
  }, [token]);

  useEffect(() => { fetchTickets(); fetchStats(); }, [fetchTickets, fetchStats]);

  // Auto-refresh tickets every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTickets(true);
      fetchStats();
      if (selectedTicket) fetchTicketDetail(selectedTicket.id, true);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchTickets, fetchStats, fetchTicketDetail, selectedTicket]);

  const openTicket = (ticket) => {
    setSelectedTicket(ticket);
    setReplyMessage("");
    fetchTicketDetail(ticket.id);
  };

  const handleReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    setIsSending(true);
    try {
      await axios.post(`${API}/admin/tickets/${selectedTicket.id}/reply`, { message: replyMessage }, { headers });
      toast.success("Réponse envoyée");
      setReplyMessage("");
      fetchTicketDetail(selectedTicket.id);
      fetchTickets();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setIsSending(false); }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      await axios.patch(`${API}/admin/tickets/${ticketId}/status`, { status }, { headers });
      toast.success(`Ticket ${status === "closed" ? "fermé" : "rouvert"}`);
      fetchTicketDetail(ticketId);
      fetchTickets();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const deleteTicket = async (ticketId) => {

    try {
      await axios.delete(`${API}/admin/tickets/${ticketId}`, { headers });
      toast.success("Ticket supprimé");
      setSelectedTicket(null);
      setTicketDetail(null);
      fetchTickets();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleEditMessage = async (ticketId, messageId) => {
    if (!editContent.trim()) return;
    try {
      await axios.patch(`${API}/admin/tickets/${ticketId}/messages/${messageId}`, { content: editContent }, { headers });
      setEditingMessageId(null);
      setEditContent("");
      fetchTicketDetail(ticketId, true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteMessage = async (ticketId, messageId) => {

    try {
      await axios.delete(`${API}/admin/tickets/${ticketId}/messages/${messageId}`, { headers });
      fetchTicketDetail(ticketId, true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === tickets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(tickets.map(t => t.id)));
    }
  };

  const bulkDelete = async () => {
    for (const id of selectedIds) {
      try { await axios.delete(`${API}/admin/tickets/${id}`, { headers }); } catch {}
    }
    toast.success(`${selectedIds.size} ticket${selectedIds.size > 1 ? "s" : ""} supprimé${selectedIds.size > 1 ? "s" : ""}`);
    setSelectedIds(new Set());
    setSelectionMode(false);
    fetchTickets();
    fetchStats();
  };

  const bulkClose = async () => {
    for (const id of selectedIds) {
      try { await axios.patch(`${API}/admin/tickets/${id}/status`, { status: "closed" }, { headers }); } catch {}
    }
    toast.success(`${selectedIds.size} ticket${selectedIds.size > 1 ? "s" : ""} fermé${selectedIds.size > 1 ? "s" : ""}`);
    setSelectedIds(new Set());
    setSelectionMode(false);
    fetchTickets();
    fetchStats();
  };

  // Detail view
  if (selectedTicket) {
    const sc = STATUS_CONFIG[ticketDetail?.status || selectedTicket.status] || STATUS_CONFIG.open;
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => { setSelectedTicket(null); setTicketDetail(null); }} className="text-zinc-400 hover:text-white p-1.5 rounded-lg hover:bg-zinc-800/50 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-white font-medium truncate">{selectedTicket.subject}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge className={`${sc.color} text-[10px]`}>{sc.label}</Badge>
              <span className="text-[10px] bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent font-medium">par {selectedTicket.username}</span>
              <span className="text-[10px] text-zinc-600">{formatDate(selectedTicket.created_at)}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {(ticketDetail?.status || selectedTicket.status) !== "closed" ? (
              <Button onClick={() => updateStatus(selectedTicket.id, "closed")} variant="outline" size="sm" className="border-zinc-700 text-zinc-400 text-xs">
                <XCircle className="w-3.5 h-3.5 mr-1" /> Fermer
              </Button>
            ) : (
              <Button onClick={() => updateStatus(selectedTicket.id, "open")} variant="outline" size="sm" className="border-zinc-700 text-emerald-400 text-xs">
                <AlertCircle className="w-3.5 h-3.5 mr-1" /> Rouvrir
              </Button>
            )}
            <Button onClick={() => deleteTicket(selectedTicket.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300 h-8 w-8 p-0">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Messages */}
        {isLoadingDetail ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
          </div>
        ) : (
          <ScrollArea className="h-[calc(100vh-340px)] min-h-[300px]">
            <div className="space-y-3 pr-2">
              {ticketDetail?.messages?.map((msg, idx) => {
                const isAdmin = msg.author_role === "admin" || msg.author_role === "super_admin";
                const isLast = idx === ticketDetail.messages.length - 1;
                const isEditing = editingMessageId === msg.id;
                return (
                  <div key={msg.id} className={`flex flex-col ${isAdmin ? "items-end" : "items-start"}`}>
                    <div className={`group/msg relative max-w-[80%] rounded-xl p-3.5 transition-colors duration-150 ${
                      isAdmin
                        ? "bg-amber-500/8 border border-amber-500/15 rounded-tr-sm shadow-[0_0_12px_rgba(245,158,11,0.03)]"
                        : "bg-zinc-900/50 border border-zinc-800/50 rounded-tl-sm"
                    }`}>
                      {/* Edit/Delete buttons on hover */}
                      <div className={`absolute ${isAdmin ? "-left-16" : "-right-16"} top-1 flex items-center gap-0.5 opacity-0 group-hover/msg:opacity-100 transition-opacity duration-200`}>
                        <button
                          onClick={() => { setEditingMessageId(msg.id); setEditContent(msg.content); }}
                          className="p-1.5 rounded-md text-zinc-500 hover:text-cyan-400 hover:bg-zinc-800 transition-colors group/edit"
                          title="Modifier"
                        >
                          <Pencil className="w-3.5 h-3.5 group-hover/edit:animate-[wiggle_0.4s_ease-in-out]" />
                        </button>
                        <button
                          onClick={() => handleDeleteMessage(selectedTicket.id, msg.id)}
                          className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-800 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isAdmin ? "bg-amber-500/20" : "bg-cyan-500/20"}`}>
                          {isAdmin ? <Shield className="w-3 h-3 text-amber-400" /> : <User className="w-3 h-3 text-cyan-400" />}
                        </div>
                        <span className={`text-[11px] font-medium ${isAdmin ? "text-amber-400" : "text-cyan-400"}`}>{msg.author}</span>
                        <span className="text-[10px] text-zinc-600">{formatRelative(msg.created_at)}</span>
                        {msg.edited_at && <span className="text-[10px] text-zinc-600 italic">modifié</span>}
                      </div>
                      {isEditing ? (
                        <div className="space-y-2">
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="w-full bg-zinc-900/80 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 resize-none focus:outline-none focus:border-cyan-500/50"
                            rows={3}
                            autoFocus
                          />
                          <div className="flex items-center gap-1.5 justify-end">
                            <button onClick={() => { setEditingMessageId(null); setEditContent(""); }} className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors">
                              <X className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleEditMessage(selectedTicket.id, msg.id)} className="p-1.5 rounded-md text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors">
                              <Check className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      )}
                    </div>
                    {isAdmin && isLast && (
                      <span className={`text-[10px] mt-1 mr-1 ${ticketDetail.read_by_user !== false ? "text-cyan-500/60" : "text-zinc-600"}`}>
                        {ticketDetail.read_by_user !== false ? "✓ Lu" : "Envoyé"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}

        {/* Reply */}
        {(ticketDetail?.status || selectedTicket.status) !== "closed" && (
          <div className="flex gap-2 pt-2 border-t border-zinc-800">
            <Input
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder="Répondre au ticket..."
              className="bg-zinc-900 border-zinc-800 text-white text-sm"
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleReply()}
            />
            <Button
              onClick={handleReply}
              disabled={isSending || !replyMessage.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-black shrink-0"
              size="sm"
            >
              {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={Ticket} label="Total" value={stats.total} color="text-white" />
          <StatCard icon={AlertCircle} label="Ouverts" value={stats.open} color="text-amber-400" />
          <StatCard icon={CheckCircle} label="Répondus" value={stats.answered} color="text-emerald-400" />
          <StatCard icon={XCircle} label="Fermés" value={stats.closed} color="text-cyan-400" />
        </div>
      )}

      {/* Filter + Title */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-white flex items-center gap-2 tracking-tight">
            <Ticket className="w-5 h-5 text-amber-500" />
            Tickets
          </h2>
          <button
            onClick={() => { setIsRefreshing(true); fetchTickets(); fetchStats(); setTimeout(() => setIsRefreshing(false), 1500); }}
            className="text-zinc-500 hover:text-amber-400 p-1.5 rounded-lg hover:bg-amber-500/10 transition-colors group/refresh"
            title="Actualiser"
          >
            <RefreshCw className={`w-4 h-4 transition-transform duration-500 ${isRefreshing ? "animate-spin" : "group-hover/refresh:rotate-[360deg]"}`} />
          </button>
          {tickets.length > 0 && (
            <button
              onClick={() => { setSelectionMode(!selectionMode); setSelectedIds(new Set()); }}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                selectionMode
                  ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  : "text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 border border-zinc-800 hover:border-amber-500/30"
              }`}
              title="Sélectionner"
            >
              <ListChecks className="w-3.5 h-3.5" />
              {selectionMode ? "Annuler" : "Sélectionner"}
            </button>
          )}
        </div>
        <div className="flex gap-1">
          {["all", "open", "answered", "closed"].map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-colors duration-200 ${
                statusFilter === f
                  ? "bg-gradient-to-r from-amber-500/20 to-amber-600/10 text-amber-400 border border-amber-500/25 shadow-[0_0_8px_rgba(245,158,11,0.08)]"
                  : "text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 border border-zinc-800/50 hover:border-amber-500/25"
              }`}
            >
              {f === "all" ? "Tous" : f === "open" ? "Ouverts" : f === "answered" ? "Répondus" : "Fermés"}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <Ticket className="w-10 h-10 text-zinc-700 mx-auto" />
          <p className="text-zinc-500 text-sm">Aucun ticket</p>
        </div>
      ) : (
        <div className="space-y-2">
          {selectionMode && (
            <div className="flex items-center justify-between bg-zinc-900/40 backdrop-blur-sm border border-zinc-800/50 rounded-xl px-4 py-2.5">
              <button
                onClick={selectAll}
                className="flex items-center gap-2 text-xs text-zinc-400 hover:text-amber-400 transition-colors"
              >
                {selectedIds.size === tickets.length ? (
                  <CheckSquare className="w-4 h-4 text-amber-400" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                {selectedIds.size === tickets.length ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-zinc-500">{selectedIds.size} sélectionné{selectedIds.size > 1 ? "s" : ""}</span>
                  <Button onClick={bulkClose} variant="outline" size="sm" className="border-zinc-700 text-zinc-400 text-xs h-7 px-2.5">
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Fermer
                  </Button>
                  <Button onClick={bulkDelete} variant="outline" size="sm" className="border-red-500/30 text-red-400 text-xs h-7 px-2.5 hover:bg-red-500/10">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                  </Button>
                </div>
              )}
            </div>
          )}
          {tickets.map((ticket) => {
            const sc = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
            const pc = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.normal;
            const StatusIcon = sc.icon;
            return (
              <div
                key={ticket.id}
                className={`w-full bg-zinc-900/30 backdrop-blur-sm border rounded-xl p-4 transition-colors duration-200 group ${
                  selectionMode && selectedIds.has(ticket.id)
                    ? "border-amber-500/30 bg-amber-500/5 shadow-[0_0_12px_rgba(245,158,11,0.04)]"
                    : "border-zinc-800/50 hover:border-zinc-700/50 hover:bg-zinc-900/50"
                }`}
              >
                <div className="flex items-start gap-3">
                  {selectionMode && (
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSelect(ticket.id); }}
                      className="shrink-0 mt-1.5 transition-colors"
                    >
                      {selectedIds.has(ticket.id) ? (
                        <CheckSquare className="w-5 h-5 text-amber-400" />
                      ) : (
                        <Square className="w-5 h-5 text-zinc-600 hover:text-zinc-400" />
                      )}
                    </button>
                  )}
                  <button onClick={() => selectionMode ? toggleSelect(ticket.id) : openTicket(ticket)} className="flex items-start gap-3 flex-1 min-w-0 text-left">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      ticket.status === "open" ? "bg-amber-500/15 border border-amber-500/20" :
                      ticket.status === "answered" ? "bg-emerald-500/15 border border-emerald-500/20" :
                      "bg-zinc-800 border border-zinc-700"
                    }`}>
                      <StatusIcon className={`w-4 h-4 ${
                        ticket.status === "open" ? "text-amber-400" :
                        ticket.status === "answered" ? "text-emerald-400" :
                        "text-zinc-500"
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-white font-medium truncate">{ticket.subject}</p>
                        <Badge className={`${sc.color} text-[9px] px-1.5 shrink-0`}>{sc.label}</Badge>
                        {ticket.priority === "high" && (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[9px] px-1.5">Urgent</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-[11px] flex items-center gap-1">
                          <User className="w-3 h-3 text-zinc-500" /> <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent font-medium">{ticket.username}</span>
                        </span>
                        <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> {ticket.message_count} message{ticket.message_count > 1 ? "s" : ""}
                        </span>
                        <span className="text-[11px] text-zinc-600 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {formatRelative(ticket.last_message_at || ticket.updated_at)}
                        </span>
                        {ticket.last_message_by === "user" && ticket.status !== "closed" && (
                          <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-[9px] px-1">En attente</Badge>
                        )}
                        {ticket.read_by_admin === false && (
                          <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0 animate-pulse" title="Non lu" />
                        )}
                      </div>
                    </div>
                  </button>
                  <div className={`flex items-center gap-1 shrink-0 transition-opacity ${selectionMode ? "hidden" : "opacity-0 group-hover:opacity-100"}`}>
                    {ticket.status !== "closed" ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); updateStatus(ticket.id, "closed"); }}
                        className="p-1.5 rounded-lg text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                        title="Fermer"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); updateStatus(ticket.id, "open"); }}
                        className="p-1.5 rounded-lg text-zinc-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                        title="Rouvrir"
                      >
                        <AlertCircle className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteTicket(ticket.id); }}
                      className="p-1.5 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
